import { computed, ref } from 'vue'
import { PricingAction } from '../constants'
import type { PartPricingItem, PricingControlState } from '../types'

export function useBillPartPricingState() {
  const state = ref<PricingControlState>({
    items: [],
    isLoading: false,
    isProcessing: false,
    error: null,
    hasUnsavedChanges: false,
  })

  // Computed properties for filtered items
  const pendingItems = computed(() => state.value.items.filter((item) => item.action === PricingAction.PENDING))

  const itemsToUpdate = computed(() => state.value.items.filter((item) => item.action === PricingAction.UPDATE))

  const itemsToKeep = computed(() => state.value.items.filter((item) => item.action === PricingAction.KEEP))

  const canProceed = computed(() => state.value.items.length > 0 && pendingItems.value.length === 0 && !state.value.isLoading && !state.value.isProcessing)

  // Enhanced canProceed that includes validation (needs to be passed validation from outside)
  const canProceedWithValidation = (validation?: { validationSummary: { value: { isValid: boolean } } }, readonly?: boolean) => {
    if (!validation) return canProceed.value
    return canProceed.value && validation.validationSummary.value.isValid && !readonly
  }

  const hasChanges = computed(() => state.value.hasUnsavedChanges || state.value.items.some((item) => item.action !== PricingAction.PENDING))

  const totalPriceImpact = computed(() => itemsToUpdate.value.reduce((total, item) => total + item.priceDifference, 0))

  const itemsWithSignificantChanges = computed(() => state.value.items.filter((item) => Math.abs(item.priceDifferencePercent) >= 10))

  // State mutation helpers
  const setLoading = (loading: boolean) => {
    state.value.isLoading = loading
  }

  const setProcessing = (processing: boolean) => {
    state.value.isProcessing = processing
  }

  const setError = (error: string | null) => {
    state.value.error = error
  }

  const setItems = (items: PartPricingItem[]) => {
    state.value.items = items
    state.value.hasUnsavedChanges = false
  }

  const updateItem = (itemId: string, updates: Partial<PartPricingItem>) => {
    const index = state.value.items.findIndex((item) => item.id === itemId)
    if (index !== -1) {
      state.value.items[index] = { ...state.value.items[index], ...updates }
      state.value.hasUnsavedChanges = true
    }
  }

  const setItemAction = (itemId: string, action: PricingAction) => {
    updateItem(itemId, { action })
  }

  const resetState = () => {
    state.value = {
      items: [],
      isLoading: false,
      isProcessing: false,
      error: null,
      hasUnsavedChanges: false,
    }
  }

  return {
    state,

    // Computed
    pendingItems,
    itemsToUpdate,
    itemsToKeep,
    canProceed,
    canProceedWithValidation,
    hasChanges,
    totalPriceImpact,
    itemsWithSignificantChanges,

    // Actions
    setLoading,
    setProcessing,
    setError,
    setItems,
    updateItem,
    setItemAction,
    resetState,
  }
}
